import { FC } from 'react';
import { Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Email,
  Delete,
  OpenInNew,
  Edit,
  Lock,
  LockOpen,
  CheckCircle,
} from '@mui/icons-material';
import {
  useListOrganisationUsers,
  useOrganisationUser,
  useUpdateOrganisationUserFirms,
  useGrantFirmAccess,
  useRevokeFirmAccess,
} from 'hooks/data/organisation/useOrganisationUsers';
import useOrganisationPlatformInvites from 'hooks/data/organisation/useOrganisationPlatformInvites';
import Table, { TableActions } from 'components/Table';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useNotification } from 'hooks/ui/useNotification';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import { NavLink } from 'react-router-dom';
import { createOrganisationAddUserRoute } from 'adminConstants/routes';
import { FirmSelector } from 'components/FirmSelector';
import { useLaunchPortalImpersonation } from 'hooks/data/impersonationAccess/useLaunchPortalImpersonation';
import api from 'lib/trpcClient';
import { Api } from 'further-types/organisation-user';
import { useOrgUserFirmAccess } from 'hooks/data/organisation/useOrgUserFirmAccess';
import OrgUserFirmAccessSelector from '../OrgUserFirmAccessSelector';

type Props = {
  organisationId: string;
};

const useStyles = makeStyles()(() => ({
  flex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  container: {
    marginBottom: '1.5rem',
  },
  title: {
    color: '#656565',
    margin: '1.5rem 0',
  },
}));

const OrganisationUsers: FC<Props> = ({ organisationId }) => {
  const { classes } = useStyles();
  const { isSuperAdmin, firmId } = useGetRole();
  const { success } = useNotification();
  const [
    createPermission,
    deletePermission,
    impersonatePermission,
    resendInvitesPermission,
  ] = useHasPermissions([
    RolePermission.CreateOrganisationUsers,
    RolePermission.DeleteOrganisationUsers,
    RolePermission.UseImpersonationAccessOrgUser,
    RolePermission.CreatePlatformInvites,
  ]);
  const deleteDisclosure = useDisclosure();
  const impersonationDisclosure = useDisclosure();
  const editFirmsDisclosure = useDisclosure();

  const editFirmAccessState = useOrgUserFirmAccess();

  const { data, isFetching } = useListOrganisationUsers({ organisationId });
  const { resendInvite } = useOrganisationPlatformInvites({
    onResendInviteSuccess: () => {
      success('Organisation invitation sent successfully.');
    },
  });
  const { remove } = useOrganisationUser({ organisationId });
  const updateFirms = useUpdateOrganisationUserFirms({ organisationId });
  const grantFirmAccess = useGrantFirmAccess({ organisationId });
  const revokeFirmAccess = useRevokeFirmAccess({ organisationId });
  const launchPortalImpersonation = useLaunchPortalImpersonation();
  const { data: eisFirm } = api.eisFirm.getById.useQuery(
    { firmId: firmId ?? '' },
    { enabled: !!firmId },
  );

  const handleImpersonation = ({ id }: { id: string }) => {
    if (isSuperAdmin) {
      impersonationDisclosure.stageAction({ id });
    } else {
      launchPortalImpersonation.orgUser({
        organisationUserId: id,
        firmDomain: eisFirm?.domain ?? '',
      });
    }
  };

  const handleEditFirms = (row: Api.OrganisationUser) => {
    editFirmAccessState.reset(row.hasAllFirmsAccess, row.firmIds);
    editFirmsDisclosure.stageAction({ id: row._id });
  };

  const getFirmAccessLabel = (row: Api.OrganisationUser) => {
    if (row.hasAllFirmsAccess) return 'All firms';
    if (row.firmIds.length === 0) return 'No access';
    return row.firmIds.map((f) => f.firmName).join(', ');
  };

  const userHasAccessToCurrentFirm = (row: Api.OrganisationUser) => {
    if (row.hasAllFirmsAccess) return true;
    return row.firmIds.some((f) => f._id === firmId);
  };

  const columns = [
    { label: 'First name', key: 'firstName' },
    { label: 'Last name', key: 'lastName' },
    { label: 'Email', key: 'email' },
    {
      label: 'Firm access',
      key: 'firms',
      render: (row: Api.OrganisationUser) => {
        if (isSuperAdmin) {
          return getFirmAccessLabel(row);
        }
        return userHasAccessToCurrentFirm(row) ? (
          <CheckCircle color="success" />
        ) : (
          'No access'
        );
      },
    },
    {
      label: 'Actions',
      key: 'actions',
      render: (row: Api.OrganisationUser) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              icon: Lock,
              color: 'primary',
              visible:
                !isSuperAdmin &&
                !!createPermission &&
                !userHasAccessToCurrentFirm(row),
              label: 'Grant firm access',
              onClick: () => grantFirmAccess.mutate(row._id),
            },
            {
              icon: Delete,
              color: 'error',
              visible:
                !!deletePermission && (isSuperAdmin || row.hasAllFirmsAccess),
              label: 'Delete',
              onClick: () => deleteDisclosure.stageAction({ id: row._id }),
            },
            {
              icon: LockOpen,
              color: 'error',
              visible:
                !!deletePermission &&
                !isSuperAdmin &&
                !row.hasAllFirmsAccess &&
                userHasAccessToCurrentFirm(row),
              label: 'Revoke access',
              onClick: () => revokeFirmAccess.mutate(row._id),
            },
            {
              icon: Email,
              color: 'primary',
              visible:
                !!resendInvitesPermission &&
                (isSuperAdmin || userHasAccessToCurrentFirm(row)),
              disabled: isSuperAdmin || resendInvite.isPending,
              label: 'Resend invitation',
              tooltip: isSuperAdmin
                ? 'Resending organisation invites is only possible for firm admin users.'
                : 'Resend invitation to this user.',
              onClick: (e) => {
                e?.preventDefault();
                resendInvite.mutate(row._id);
              },
            },
            {
              icon: OpenInNew,
              color: 'primary',
              visible:
                !!impersonatePermission &&
                (isSuperAdmin || userHasAccessToCurrentFirm(row)),
              label: 'Launch portal',
              onClick: () => handleImpersonation({ id: row._id }),
            },
            {
              icon: Edit,
              color: 'primary',
              visible: isSuperAdmin && !!createPermission,
              label: 'Edit firm access',
              onClick: () => handleEditFirms(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className={classes.container}>
      <div className={classes.flex}>
        <h2 className={classes.title}>Organisation users</h2>
        {createPermission && (
          <NavLink to={createOrganisationAddUserRoute(organisationId)}>
            <Button color="primary" variant="outlined">
              Add user
            </Button>
          </NavLink>
        )}
      </div>
      <Table
        loading={isFetching}
        tablebody={data ?? []}
        columns={columns}
        emptyMessage="No organisation users found"
      />
      <AlertDialog
        open={deleteDisclosure.isOpen}
        title="Delete organisation user"
        content="Are you sure you want to delete this user?"
        onClose={deleteDisclosure.onClose}
        onConfirm={() => {
          remove.mutate(deleteDisclosure.actionProps.id);
          deleteDisclosure.onClose();
        }}
        btnLabels={{ confirm: 'Delete user', cancel: 'Cancel' }}
      />
      {impersonatePermission && isSuperAdmin && (
        <AlertDialog
          open={impersonationDisclosure.isOpen}
          onClose={impersonationDisclosure.onClose}
          title="Choose which firms portal to view"
          content={
            <FirmSelector
              onChange={(_firmId, firm) => {
                if (!firm?.domain) {
                  return;
                }
                launchPortalImpersonation.orgUser({
                  organisationUserId: impersonationDisclosure.actionProps.id,
                  firmDomain: firm.domain,
                });
                impersonationDisclosure.onClose();
              }}
            />
          }
          btnLabels={{ cancel: 'Close' }}
        />
      )}
      {isSuperAdmin && createPermission && (
        <AlertDialog
          open={editFirmsDisclosure.isOpen}
          title="Edit firm access"
          content={
            <OrgUserFirmAccessSelector
              firmAccess={editFirmAccessState.firmAccess}
              selectedFirms={editFirmAccessState.selectedFirms}
              eisFirms={editFirmAccessState.eisFirms}
              onFirmAccessChange={editFirmAccessState.setFirmAccess}
              onSelectedFirmsChange={editFirmAccessState.setSelectedFirms}
            />
          }
          onClose={editFirmsDisclosure.onClose}
          onConfirm={() => {
            updateFirms.mutate({
              orgUserId: editFirmsDisclosure.actionProps.id,
              firmIds: editFirmAccessState.firmIds,
              hasAllFirmsAccess: editFirmAccessState.hasAllFirmsAccess,
            });
            editFirmsDisclosure.onClose();
          }}
          btnLabels={{ confirm: 'Save', cancel: 'Cancel' }}
        />
      )}
    </div>
  );
};

export default OrganisationUsers;
